import { formatTimeDisplay } from '../../utils/timeUtils';
import type { InstructionStepDto, LessonDetailDto } from './types';
import { LYW_BRAND } from '../../constants/brandPalette';
import { renderSupportingMaterialMarkdownToHtml, getSupportingMaterialPrintStyles } from './renderSupportingMaterialMarkdown';
import { formatStepBadgeFromIds, resolveLessonMaterials, sortInstructionSteps, stripDuplicateMaterialTitleFromContent, stripWorksheetPreamble } from './instructionStepUtils';
import { getDisplayVideoOptions, isVideoRemovedFromYouTube } from './mediaReviewUtils';
import { VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE } from '../LessonVideoPlayer';
import { inlineAuthenticatedBlobImagesInHtml } from './printBlobImages';
import { collectRasterFiguresFromSteps } from './supportingMaterialContentSegments';

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export type LessonPrintOptions = {
    lesson: LessonDetailDto;
    statusLabel: string;
    /** When true, appends " [DRAFT]" after the title (e.g. lessons printed from Review). */
    draft?: boolean;
};

function buildInstructionStepsPrintHtml(steps: InstructionStepDto[] | undefined): string {
    const sorted = sortInstructionSteps(steps ?? []);
    if (!sorted.length) return '';

    const items = sorted
        .map(step => {
            const stepNumber = step.e251_StepNumber ?? 0;
            const summary = step.e251_Summary?.trim() || `Step ${stepNumber}`;
            const detail = step.e251_DetailedInstructions?.trim();
            return `
                <div class="instruction-step">
                    <div class="instruction-step-title">${escapeHtml(`${stepNumber}. ${summary}`)}</div>
                    ${detail ? `<div class="section-content">${escapeHtml(detail)}</div>` : ''}
                </div>
            `;
        })
        .join('');

    return `
        <div class="section">
            <h2>Instructions</h2>
            ${items}
        </div>
    `;
}

function materialTypeLabel(typeKey?: string): string {
    switch (typeKey) {
        case '10':
            return 'Worksheet';
        case '20':
            return 'Reading';
        case '30':
            return 'Video';
        default:
            return 'Material';
    }
}

function buildSupportingMaterialsPrintHtml(lesson: LessonDetailDto, draft = false): string {
    const materials = resolveLessonMaterials(lesson);
    if (!materials.length) return '';

    const sorted = [...materials].sort(
        (a, b) => (a.e251_SortOrder ?? 0) - (b.e251_SortOrder ?? 0)
    );

    const items = sorted
        .map(material => {
            const title = material.e251_Title || 'Supporting material';
            const typeLabel = materialTypeLabel(material.e251_MaterialType);
            const stepBadge = formatStepBadgeFromIds(
                material.e251_RelatedStepIds,
                lesson.e251_InstructionSteps
            );
            const contentHtml =
                material.e251_MaterialType === '30'
                    ? `<p>${escapeHtml(material.e251_Content || '')}</p>${
                          isVideoRemovedFromYouTube(material)
                              ? `<p>${escapeHtml(VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE)}</p>`
                              : material.e251_VideoOptions?.length
                              ? `<p><strong>${draft ? 'Video options for review' : 'Videos'}:</strong></p><ol>${getDisplayVideoOptions(material, { includeAllForReview: draft })
                                    .map(
                                        (option, index) =>
                                            `<li><a href="${escapeHtml(option.e251_VideoUrl || '')}">${escapeHtml(
                                                option.e251_VideoTitle || `Option ${index + 1}`
                                            )}</a>${
                                                option.e251_VideoChannelTitle
                                                    ? ` (${escapeHtml(option.e251_VideoChannelTitle)})`
                                                    : ''
                                            }</li>`
                                    )
                                    .join('')}</ol>`
                              : material.e251_Url
                                ? `<p><strong>Video:</strong> <a href="${escapeHtml(material.e251_Url)}">${escapeHtml(material.e251_Url)}</a>${
                                      material.e251_VideoChannelTitle
                                          ? ` (${escapeHtml(material.e251_VideoChannelTitle)})`
                                          : ''
                                  }</p>`
                                : ''
                      }`
                    : renderSupportingMaterialMarkdownToHtml(
                          stripDuplicateMaterialTitleFromContent(
                              material.e251_MaterialType === '10'
                                  ? stripWorksheetPreamble(material.e251_Content || '')
                                  : material.e251_Content || '',
                              material.e251_Title
                          ),
                          collectRasterFiguresFromSteps(lesson.e251_InstructionSteps)
                      );

            return `
                <div class="supporting-material">
                    <div class="supporting-material-title">${escapeHtml(title)}</div>
                    <div class="supporting-material-meta">
                        ${escapeHtml(typeLabel)}${stepBadge ? ` | ${escapeHtml(stepBadge)}` : ''}
                    </div>
                    ${contentHtml}
                </div>
            `;
        })
        .join('');

    return `
        <div class="section">
            <h2>Supporting Materials</h2>
            ${items}
        </div>
    `;
}


export function buildLessonPrintHtml({ lesson, statusLabel, draft = false }: LessonPrintOptions): string {
    const lessonTitle = lesson.e251_LessonTitle || 'Untitled Lesson';
    const displayTitle = draft ? `${lessonTitle} [DRAFT]` : lessonTitle;
    const lessonDuration = lesson.e251_LessonDuration || 0;
    const logoUrl = `${window.location.origin}/z30-images/logo_lyw_full_400x200.png`;

    return `
        <!DOCTYPE html>
        <html>
        <head>
            <title>${escapeHtml(displayTitle)}</title>
            <style>
                @media print {
                    @page {
                        margin: 1in;
                    }
                }
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    line-height: 1.6;
                    color: #333;
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }
                h1 {
                    color: #1a1a1a;
                }
                h2 {
                    color: #374151;
                    margin-top: 30px;
                    margin-bottom: 10px;
                    font-size: 1.2em;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .lesson-info {
                    background: #f9fafb;
                    padding: 15px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    border-left: 4px solid ${LYW_BRAND.actionBlue};
                }
                .lesson-info p {
                    margin: 5px 0;
                }
                .section {
                    margin-bottom: 25px;
                }
                .section-content {
                    white-space: pre-wrap;
                    background: #ffffff;
                    padding: 15px;
                    border-radius: 4px;
                    border: 1px solid #e5e7eb;
                }
                .activity {
                    margin-bottom: 20px;
                    padding: 15px;
                    background: #f9fafb;
                    border-left: 3px solid ${LYW_BRAND.confirmAction};
                    border-radius: 4px;
                }
                .activity-title {
                    font-weight: 600;
                    color: ${LYW_BRAND.confirmActionDark};
                    margin-bottom: 8px;
                }
                .activity-details {
                    font-size: 0.9em;
                    color: #6b7280;
                    margin-top: 5px;
                }
                .no-content {
                    color: #9ca3af;
                    font-style: italic;
                }
                .header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .header-title {
                    color: #1a1a1a;
                    margin: 0;
                    flex: 1;
                }
                .header-logo {
                    height: 60px;
                    width: auto;
                    margin-left: 20px;
                }
                ${getSupportingMaterialPrintStyles()}
                .supporting-material {
                    margin-bottom: 20px;
                    padding: 15px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 4px;
                    page-break-inside: avoid;
                }
                .supporting-material-title {
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 8px;
                }
                .supporting-material-meta {
                    font-size: 0.85em;
                    color: #6b7280;
                    margin-bottom: 10px;
                }
            </style>
        </head>
        <body>
            <div class="header-row">
                <h1 class="header-title">${escapeHtml(displayTitle)}</h1>
                <img src="${logoUrl}" 
                     alt="Learn Your Way Logo" 
                     class="header-logo"
                     onerror="this.style.display='none'">
            </div>
            
            <div class="lesson-info">
                <p><strong>Status:</strong> ${escapeHtml(statusLabel)}</p>
                <p><strong>Total Duration:</strong> ${escapeHtml(formatTimeDisplay(lessonDuration))}</p>
            </div>

            ${lesson.e251_LessonSummary ? `
                <div class="section">
                    <h2>Summary</h2>
                    <div class="section-content">${escapeHtml(lesson.e251_LessonSummary)}</div>
                </div>
            ` : ''}

            ${buildInstructionStepsPrintHtml(lesson.e251_InstructionSteps)}

            ${lesson.e251_LessonMaterials ? `
                <div class="section">
                    <h2>Materials to Provide</h2>
                    <div class="section-content">${escapeHtml(lesson.e251_LessonMaterials)}</div>
                </div>
            ` : ''}

            ${buildSupportingMaterialsPrintHtml(lesson, draft)}

            ${!lesson.e251_LessonSummary && !(lesson.e251_InstructionSteps?.length) && !lesson.e251_LessonMaterials && !resolveLessonMaterials(lesson).length ? `
                <div class="section">
                    <p class="no-content">No additional lesson content available.</p>
                </div>
            ` : ''}
        </body>
        </html>
    `;
}

export async function openLessonPrintWindow(
    html: string,
    idToken?: string | null
): Promise<void> {
    const printHtml = await inlineAuthenticatedBlobImagesInHtml(html, idToken);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
        printWindow.print();
    }, 250);
}
